import { RouteEditModelBase } from "./route-edit-model";
import { VoyageRoutePointFeature, VoyageRoute, VoyageRouteLineFeature, VoyageRoutingModel, RoutingJobOptions, RoutePassagePlanProperties, FeatureOrigin } from "./voyage-routing-model";
import { CoordinatesUtils } from "../utils/coordinates-utils";
import { getLegTypeFromDist } from "../utils/map-utils";
import { VoyageDialogPopupManager } from "../../local_modules/voyage-dialog-popup/src/voyage-dialog-popup-manager";
import { getWaypointsFromRoute } from '../utils/helpers';
import { EcaSecaAreasUtilities } from "../utils/zones/eca-seca-areas-utils";

// Model for route editing 
export class RouteEditModelImpl extends RouteEditModelBase {

    get uiRouteIndex() {
        return this._uiRouteIndex;
    }

    clear() {
        this._revert = [];
        this._waypoints = [];
        this._uiRouteIndex = -1;
        this._scheduleId = "";
        this._routeId = "";
        this._dispatchNewRouteEvent();
    }

    editRoute(routeId: string, uiRouteIndex: number, scheduleId: string, route: VoyageRoute, setIsPassedUpdate: boolean, etd?: number, name?: string) {
        this.clear();
        this._uiRouteIndex = uiRouteIndex;
        this._scheduleId = scheduleId;
        this._setIsPassedUpdate = !!setIsPassedUpdate;
        this._etd = etd;
        this._origRoute = route;
        this._routeId = routeId;
        this._routeName = name ?? route.name ?? "";
        const allWaypoints = getWaypointsFromRoute(route.route_geometry);
        this.setNewRoute(allWaypoints);
    }

    setNewRoute(waypoints: VoyageRoutePointFeature[]) {
        this._normalizeStoppagePairs(waypoints);

        //recalculate the values
        for (let i = 0; i < waypoints.length - 1; i++) {
            const wp = waypoints[i];
            const nextWp = waypoints[i + 1];
            const distance = CoordinatesUtils.calcDistance(wp.geometry.coordinates, nextWp.geometry.coordinates);
            wp.properties.course = CoordinatesUtils.calcCourse(wp.geometry.coordinates, nextWp.geometry.coordinates);
            wp.properties.distance = wp.properties.leg_type === "GreatCircle" ? distance.gc : distance.rl;
        }

        this._revert.push(JSON.parse(JSON.stringify(waypoints)));

        // On add/remove, clear selection.
        if (waypoints.length !== this._waypoints.length) {
            this.selectedWaypointIndex = -1;
        }

        this._waypoints = JSON.parse(JSON.stringify(waypoints));
        this._dispatchNewRouteEvent();
    }

    get selectedWaypointIndex() {
        return this._selectedWaypointIndex;
    }

    set selectedWaypointIndex(index: number) {
        if (index >= 0 && index < this._waypoints.length) {
            this._selectedWaypointIndex = index;
        } else {
            this._selectedWaypointIndex = -1;
        }

        this.dispatchEvent(new CustomEvent(`waypoint-selected`, { detail: { index: this._selectedWaypointIndex } }));
    }

    revert() {
        if (!this.canRevert()) {
            return;
        }

        this._revert.pop();
        this.setNewRoute(this._revert.pop()!);
    }

    canRevert() {
        return this._revert.length > 1;
    }

    getWaypoints() {
        return this._waypoints;
    }

    async saveAndCalcRoute(calcRoute: VoyageRoute) {
        /**
         * we don't want to fetch the best practice route if `create new route` was clicked.
         * it was done only for the routeIndex0 as we are getting best practice route for the first route
         */
        if (this._uiRouteIndex === 0) {
            this.dispatchEvent(new CustomEvent(`create-user-route`, { detail: this._scheduleId }));
        }

        //Apply mandatory ECA/SECA areas waypoints
        const areaUtilities: EcaSecaAreasUtilities = new EcaSecaAreasUtilities();
        let modifiedRoute: VoyageRoute;
        try {
            modifiedRoute = await areaUtilities.applyToRoute(calcRoute);
        } catch (error) {
            VoyageDialogPopupManager.createDialog({
                headerText: "ECA/SECA Zones Unavailable",
                bodyText: "Could not fetch ECA/SECA zone data. ECA/SECA zones could not be applied and route could not be calculated.\nPlease try again later, if it continues to fail, please contact support.",
                noCloseButton: true
            });
            throw error;
        }

        this._revert = [];
        modifiedRoute.name = this._routeName;

        await VoyageRoutingModel!.saveRoute(modifiedRoute, this._uiRouteIndex, this._scheduleId, this._routeId);
    }

    private _waypoints: VoyageRoutePointFeature[] = [];
    private _revert: VoyageRoutePointFeature[][] = [];
    private _uiRouteIndex: number = -1;
    private _routeId: string = "";
    private _scheduleId: string = "";
    private _selectedWaypointIndex: number = -1;
    private _setIsPassedUpdate: boolean = false;
    private _etd: number | undefined = undefined;
    private _routeName = "";
    private _origRoute: VoyageRoute | null = null;

    private _dispatchNewRouteEvent() {
        this.dispatchEvent(new CustomEvent(`route-changed`, { detail: { waypoints: this._waypoints } }));
    }

    private _normalizeStoppagePairs(waypoints: VoyageRoutePointFeature[]): void {
        for (let i = 0; i < waypoints.length - 1; i++) {
            const stoppagePoint = waypoints[i];
            const stoppageEndPoint = waypoints[i + 1];
            if (
                stoppagePoint.properties.is_stoppage_point !== true ||
                stoppagePoint.properties.stoppage_end === true ||
                stoppageEndPoint.properties.is_stoppage_point !== true ||
                stoppageEndPoint.properties.stoppage_end !== true
            ) {
                continue;
            }

            stoppageEndPoint.geometry.coordinates = [...stoppagePoint.geometry.coordinates];
            if (stoppageEndPoint.properties.leg_type) {
                stoppagePoint.properties.leg_type = stoppageEndPoint.properties.leg_type;
            } else if (stoppagePoint.properties.leg_type) {
                stoppageEndPoint.properties.leg_type = stoppagePoint.properties.leg_type;
            }
        }
    }


    generateRouteGeometry(): (Array<VoyageRoutePointFeature | VoyageRouteLineFeature>) {
        if (!this._waypoints || this._waypoints.length < 2) {
            throw "Invalid waypoints";
        }

        const route_geometry: Array<VoyageRoutePointFeature | VoyageRouteLineFeature> = [];

        for (let i = 0; i < this._waypoints.length; i++) {
            const { leg_type, showAll, ...pointProperties } = this._waypoints[i].properties;
            const wp: VoyageRoutePointFeature = {
                ...this._waypoints[i],
                geometry: {
                    ...this._waypoints[i].geometry,
                    coordinates: [...this._waypoints[i].geometry.coordinates]
                },
                properties: pointProperties
            };
            route_geometry.push(wp);

            if (i < this._waypoints.length - 1) {
                // Add LineString
                const from = this._waypoints[i].geometry.coordinates;
                const to = this._waypoints[i + 1].geometry.coordinates;
                const line = <VoyageRouteLineFeature>{
                    type: "Feature",
                    geometry: {
                        coordinates: [from, to],
                        type: "LineString",
                    },
                    properties: {
                        leg_type: this._waypoints[i].properties.leg_type ? this._waypoints[i].properties.leg_type : getLegTypeFromDist(from, to)
                    }
                };

                if (this._waypoints[i].properties.isPassed) {
                    // Try to find the original LineString and preserve the properties from there (keeping the new leg_type since it
                    // could have been changed)
                    const origLine = this._getOrigLineString(from, to);
                    if (origLine) {
                        const leg_type = line.properties.leg_type;
                        line.properties = { ...origLine.properties };
                        line.properties.leg_type = leg_type;
                    }
                }

                if (line.properties.distance === undefined) {
                    const dist = CoordinatesUtils.calcDistance(from, to);
                    line.properties.distance = line.properties.leg_type === "Rhumbline" ? dist.rl : dist.gc;
                }

                route_geometry.push(line);
            }
        }

        return route_geometry;
    }

    generateRoute(): VoyageRoute {
        if (!this._waypoints || this._waypoints.length < 2) {
            throw "Invalid waypoints";
        }

        const route_geometry: Array<VoyageRoutePointFeature | VoyageRouteLineFeature> = [];

        for (let i = 0; i < this._waypoints.length; i++) {
            const { leg_type, showAll, ...pointProperties } = this._waypoints[i].properties;
            const wp: VoyageRoutePointFeature = {
                ...this._waypoints[i],
                geometry: {
                    ...this._waypoints[i].geometry,
                    coordinates: [...this._waypoints[i].geometry.coordinates]
                },
                properties: { ...pointProperties, origin: FeatureOrigin.Bridge }
            };
            route_geometry.push(wp);

            if (i < this._waypoints.length - 1) {
                // Add LineString
                const from = this._waypoints[i].geometry.coordinates;
                const to = this._waypoints[i + 1].geometry.coordinates;
                const line = <VoyageRouteLineFeature>{
                    type: "Feature",
                    geometry: {
                        coordinates: [from, to],
                        type: "LineString",
                    },
                    properties: {
                        leg_type: this._waypoints[i].properties.leg_type ? this._waypoints[i].properties.leg_type :
                            getLegTypeFromDist(from, to)
                    }
                };

                if (this._waypoints[i].properties.isPassed || this._waypoints[i].properties.is_stoppage_point) {
                    // Try to find the original LineString and preserve the properties from there (keeping the new leg_type since it
                    // could have been changed)
                    const origLine = this._getOrigLineString(from, to);
                    if (origLine) {
                        const leg_type = line.properties.leg_type;
                        line.properties = { ...origLine.properties };
                        line.properties.leg_type = leg_type;
                    }
                }

                if (line.properties.distance === undefined) {
                    const dist = CoordinatesUtils.calcDistance(from, to);
                    line.properties.distance = line.properties.leg_type === "Rhumbline" ? dist.rl : dist.gc;
                }

                route_geometry.push(line);
            }
        }

        const result: VoyageRoute = { route_geometry, routeCreatedBy: "bridge", routeId: this._routeId, name: this._routeName};
        if (this._setIsPassedUpdate) {
            result.isPassedUpdate = new Date().toISOString();
        }
        return result;
    }

    private _getOrigLineString(from: [number, number], to: [number, number]): VoyageRouteLineFeature | null {
        const origLine = <VoyageRouteLineFeature | undefined>this._origRoute!.route_geometry.find(f => {
            if (f.geometry.type === "LineString") {
                const margin = 0.0001;
                const coords = f.geometry.coordinates;
                return CoordinatesUtils.isCoordinatesEqual(coords[0], from, margin) &&
                    CoordinatesUtils.isCoordinatesEqual(coords[1], to, margin);
            }
            return false;
        });

        return origLine ? origLine : null;
    }

    private _handleDuplicateWPs(waypoints: VoyageRoutePointFeature[]) {
        // For all duplicate waypoints, remove the first occurence if both are of the same type
        // Otherwise, prefer to keep hard waypoints
        // Any waypoints between two duplicates will be removed as well
        // If we remove any hard waypoints, put up a warning dialog

        let hardWPRemoved = false;

        for (let i = 0; i < waypoints.length - 1; i++) {
            const coords = waypoints[i].geometry.coordinates;
            let duplicateIdx: number = -1;
            for (let j = i + 1; j < waypoints.length; j++) {
                if (coords[0] === waypoints[j].geometry.coordinates[0] && coords[1] === waypoints[j].geometry.coordinates[1]) {
                    duplicateIdx = j;
                }
            }

            if (duplicateIdx != -1) {
                let removeFirstOccurence: boolean = true;
                if (waypoints[i].properties.type === "Hard" && waypoints[duplicateIdx].properties.type !== "Hard") {
                    removeFirstOccurence = false;
                }

                let removedWPs;
                if (removeFirstOccurence) {
                    removedWPs = waypoints.splice(i, duplicateIdx - i);
                }
                else {
                    removedWPs = waypoints.splice(i + 1, duplicateIdx - i);
                }

                if (removedWPs.some(wp => wp.properties.type === "Hard")) {
                    hardWPRemoved = true;
                }
            }
        }

        if (hardWPRemoved) {
            VoyageDialogPopupManager.createDialog({
                headerText: "Warning: Duplicate waypoint removed",
                bodyText: "One or more duplicate waypoint(s) detected and removed. Please check the route before saving",
                noCloseButton: true
            });
        }
    }
}
